import type { User } from "@supabase/supabase-js";

import type { UserProfile } from "../hooks/useSupabaseAuth";

export const getUserDisplayName = (
  profile: UserProfile | null,
  user: User | null,
) => {
  if (profile?.full_name) {
    return profile.full_name;
  }

  const metaName =
    (user?.user_metadata?.full_name as string | undefined) ||
    (user?.user_metadata?.name as string | undefined);
  if (metaName) {
    return metaName;
  }

  if (user?.email) {
    return user.email.split("@")[0];
  }

  return "there";
};

export const getUserAvatarUrl = (
  profile: UserProfile | null,
  user: User | null,
) => {
  return (
    profile?.avatar_url ||
    (user?.user_metadata?.avatar_url as string | undefined) ||
    null
  );
};

export const getUserInitials = (label: string) => {
  const trimmed = label.trim();
  if (!trimmed) {
    return "IW";
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

// ── User display cache (localStorage) ────────────────────────────────────────
// Caches avatar/name/initials so returning users see them instantly on page load
// instead of waiting for the async session + profile fetch to complete.

const USER_DISPLAY_CACHE_KEY = "ideaweave:userDisplay";

interface UserDisplayCache {
  displayName: string;
  avatarUrl: string | null;
  initials: string;
}

export const readUserDisplayCache = (): UserDisplayCache | null => {
  try {
    const raw = localStorage.getItem(USER_DISPLAY_CACHE_KEY);
    return raw ? (JSON.parse(raw) as UserDisplayCache) : null;
  } catch {
    return null;
  }
};

export const writeUserDisplayCache = (data: UserDisplayCache): void => {
  try {
    localStorage.setItem(USER_DISPLAY_CACHE_KEY, JSON.stringify(data));
  } catch {
    // localStorage may be full or blocked – silently ignore
  }
};

export const clearUserDisplayCache = (): void => {
  try {
    localStorage.removeItem(USER_DISPLAY_CACHE_KEY);
  } catch {
    // silently ignore
  }
};
